import { type FC, useRef, useEffect, useState, Children } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { OnaThinking } from "@/components/ona/OnaThinking";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { AgentMessageFragment } from "@/hooks/use-stream-conversation";

export type TodoContainerProps = {
    todoId: string;
    todoTitle: string;
    todoStatus: AgentResponseBlock_TodoItem_Phase;
    children: React.ReactNode;
    isLast: boolean;
    hasPreviousTodo?: boolean;
    hasNextTodo?: boolean;
    fragmentCount?: number;
    fragments?: AgentMessageFragment[];
    currentFragmentText?: string;
};

export const TodoContainer: FC<TodoContainerProps> = ({
    todoId,
    todoTitle,
    todoStatus,
    children,
    isLast,
    hasPreviousTodo = false,
    hasNextTodo = false,
    fragmentCount,
    currentFragmentText,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    const isCompleted = todoStatus === AgentResponseBlock_TodoItem_Phase.DONE;
    const isInProgress = todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const isCleared = todoStatus === AgentResponseBlock_TodoItem_Phase.CLEARED;
    
    // Animation state management - initialize based on current status
    const [animationState, setAnimationState] = useState<"entering" | "thinking" | "leaving">(() => {
        // If already completed on mount, skip animations
        if (isCompleted) return "leaving";
        // If in progress on mount, start with entering
        if (isInProgress) return "entering";
        return "entering";
    });
    const [showCheckmark, setShowCheckmark] = useState(isCompleted);
    const [isOnaExiting, setIsOnaExiting] = useState(false);
    const [hideDoneText, setHideDoneText] = useState(false);
    const prevStatusRef = useRef(todoStatus);
    
    // Check if children are empty (no fragments to display)
    const hasContent = Children.toArray(children).some(child => {
        if (child && typeof child === 'object' && 'props' in child) {
            const childProps = child.props as { children?: React.ReactNode };
            const grandChildren = Children.toArray(childProps.children || []);
            return grandChildren.length > 0;
        }
        return false;
    });

    // Auto-scroll to bottom when in progress and is last
    useEffect(() => {
        if (isInProgress && isLast && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    });

    // Handle status changes and animation transitions
    useEffect(() => {
        const prevStatus = prevStatusRef.current;
        prevStatusRef.current = todoStatus;

        if (isInProgress && prevStatus !== AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
            // Todo just started - play enter animation
            setAnimationState("entering");
            setShowCheckmark(false);
            setHideDoneText(false);
        } else if (isCompleted && prevStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
            // Todo just completed - play leave animation
            setAnimationState("leaving");
            setIsOnaExiting(true);
            setHideDoneText(false);
            
            // Start checkmark fade-in during the latter part of the exit animation
            // Exit animation is ~1050ms (frames 420-525 at 100fps)
            // Start fade at ~60% through (630ms) for tight transition
            const checkmarkTimer = setTimeout(() => {
                setShowCheckmark(true);
            }, 630);
            
            // Hide "Done" text after fade-out completes (630ms + 200ms fade = 830ms)
            const hideDoneTimer = setTimeout(() => {
                setHideDoneText(true);
            }, 830);
            
            return () => {
                clearTimeout(checkmarkTimer);
                clearTimeout(hideDoneTimer);
            };
        }
    }, [todoStatus, isInProgress, isCompleted]);

    // Todos are collapsed by default, no auto-collapse needed

    // Handle animation callbacks
    const handleEnterComplete = () => {
        // Enter animation done, transition to thinking loop
        setAnimationState("thinking");
    };

    const handleExitComplete = () => {
        // Exit animation done, ensure checkmark is visible
        // (checkmark fade-in starts during the exit animation via timeout)
        if (!showCheckmark) {
            setShowCheckmark(true);
        }
        
        // Clear the exiting flag so height can collapse
        setIsOnaExiting(false);
        
        // If there's a next todo, it will start its enter animation
        // This creates the "Ona moves between frames" effect
    };

    const getStatusIcon = () => {
        if (isCompleted) {
            // Cross-fade: show both during transition
            return (
                <div className="relative size-5">
                    {!showCheckmark && (
                        <div className="absolute inset-0">
                            <OnaThinking
                                state={animationState}
                                onExitComplete={handleExitComplete}
                                onEnterComplete={handleEnterComplete}
                                size="sm"
                                className="!size-5"
                            />
                        </div>
                    )}
                    <IconCircleCheckedFilled 
                        className={cn(
                            "size-5 text-content-green transition-opacity duration-200 ease-out",
                            showCheckmark ? "opacity-100" : "opacity-0"
                        )} 
                    />
                </div>
            );
        }
        if (isInProgress) {
            return (
                <OnaThinking
                    state={animationState}
                    onExitComplete={handleExitComplete}
                    onEnterComplete={handleEnterComplete}
                    size="sm"
                    className="!size-5"
                />
            );
        }
        return null;
    };

    const getStatusLabel = () => {
        if (isCompleted) return "Completed";
        if (isInProgress) return "In progress";
        if (isCleared) return "Cleared";
        return "Pending";
    };

    if (isCleared) {
        return null;
    }

    // Determine border radius based on position
    const getBorderRadius = () => {
        if (hasPreviousTodo && hasNextTodo) {
            return "rounded-none";
        } else if (hasPreviousTodo) {
            return "rounded-b-lg";
        } else if (hasNextTodo) {
            return "rounded-t-lg";
        } else {
            return "rounded-lg";
        }
    };

    // Determine header border radius
    const getHeaderBorderRadius = () => {
        if (isOpen) {
            if (hasPreviousTodo) {
                return "rounded-none";
            } else {
                return "rounded-t-lg";
            }
        } else {
            return getBorderRadius();
        }
    };

    // Determine margin
    const getMargin = () => {
        if (hasPreviousTodo) {
            if (hasNextTodo) {
                return "mt-0 mb-0 border-t-0";
            } else {
                return "mt-0 mb-4 border-t-0";
            }
        } else if (hasNextTodo) {
            return "mt-0 mb-0";
        } else {
            return "mb-4";
        }
    };

    return (
        <div
            id={`todo-${todoId}`}
            className={cn(
                "w-full border border-border-base bg-surface-pure",
                getBorderRadius(),
                getMargin()
            )}
        >
            {/* Todo Header */}
            <button
                onClick={() => {
                    const canExpand = hasContent && (fragmentCount === undefined || fragmentCount > 0);
                    if (canExpand) setIsOpen(!isOpen);
                }}
                className={cn(
                    "flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-all duration-300 ease-out",
                    hasContent && (fragmentCount === undefined || fragmentCount > 0) && "hover:bg-surface-muted",
                    getHeaderBorderRadius(),
                    (!hasContent || (fragmentCount !== undefined && fragmentCount === 0)) && "cursor-default"
                )}
                aria-expanded={hasContent && (fragmentCount === undefined || fragmentCount > 0) ? isOpen : undefined}
                aria-label={`${getStatusLabel()}: ${todoTitle}`}
                data-tracking-id="todo-container-toggle"
                disabled={!hasContent || (fragmentCount !== undefined && fragmentCount === 0)}
            >
                {/* Left side: Status icon + Text content (title + fragment preview) */}
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                        {getStatusIcon()}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0">
                        {isInProgress && currentFragmentText ? (
                            <div className="flex min-w-0 flex-1 flex-col gap-0">
                                <TextShimmer className="min-w-0 truncate text-base text-content-primary">
                                    {todoTitle}
                                </TextShimmer>
                                <TextShimmer className="min-w-0 truncate text-sm font-mono">
                                    {currentFragmentText}
                                </TextShimmer>
                            </div>
                        ) : isOnaExiting ? (
                            <div className="flex min-w-0 flex-1 flex-col gap-0">
                                <span className="min-w-0 truncate text-base text-content-primary">
                                    {todoTitle}
                                </span>
                                {!hideDoneText && (
                                    <span 
                                        className={cn(
                                            "min-w-0 truncate text-sm font-mono text-content-tertiary transition-opacity duration-200 ease-out",
                                            showCheckmark && "opacity-0"
                                        )}
                                    >
                                        Done
                                    </span>
                                )}
                            </div>
                        ) : (
                            <h2
                                className={cn(
                                    "min-w-0 truncate text-base text-content-primary transition-colors duration-300",
                                    isCompleted && "text-content-tertiary"
                                )}
                            >
                                {todoTitle}
                            </h2>
                        )}
                    </div>
                </div>

                {/* Right side: Controls (chevron only) - vertically centered */}
                <div className="flex flex-shrink-0 items-center">
                    <IconChevronDown
                        size="sm"
                        className={cn(
                            "flex-shrink-0 transition-transform duration-200",
                            isOpen && "rotate-180",
                            (!hasContent || (fragmentCount !== undefined && fragmentCount === 0)) && "opacity-40 cursor-not-allowed"
                        )}
                    />
                </div>
            </button>

            {/* Todo Content - Simple version without complex animations */}
            {isOpen && hasContent && (
                <div
                    ref={scrollContainerRef}
                    className={cn(
                        "w-full overflow-y-auto border-t border-border-base px-4 py-2 text-base",
                        "max-h-[40vh]"
                    )}
                >
                    {children}
                </div>
            )}
        </div>
    );
};
